import Component from "@glimmer/component";
import { restartableTask, task, timeout } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import { service } from "@ember/service";
import ENV from "glesys-controlpanel/config/environment";
import { on } from "@ember/modifier";
import { not } from "ember-truth-helpers";
import { t } from "ember-intl";
import Header from "glesys-controlpanel/components/g-modal/header";
import Body from "glesys-controlpanel/components/g-modal/body";
import Footer from "glesys-controlpanel/components/g-modal/footer";
import modalClose from "glesys-controlpanel/helpers/modal-close";
import TaskForm from "glesys-controlpanel/components/task-form";
import Label from "glesys-controlpanel/components/label";
import ResourceSlider from "glesys-controlpanel/components/resource-slider";
import AlertMessage from "glesys-controlpanel/components/alert-message";
import GButton from "glesys-controlpanel/components/g-button";
import CostCompact from "glesys-controlpanel/components/cost/compact";
import IconsLoadingSpinner from "glesys-controlpanel/components/icons/loading-spinner";
import { sizeSliderOptions } from "glesys-controlpanel/utils/size-slider-options";

export default class ModalsBlockStorageResize extends Component {
  @service modal;
  @service http;
  @service abilities;

  sizeOptions = sizeSliderOptions(this.volume.sizeInGib, this.volume.tier.maxsizeingib, 10);

  @tracked selectedSize = this.sizeOptions[0];
  @tracked cost;

  constructor() {
    super(...arguments);

    this.fetchCostTask.perform();
  }

  get task() {
    return this.args.params.task;
  }

  get volume() {
    return this.args.params.volume;
  }

  get isAtMax() {
    return this.volume.sizeInGib >= this.volume.tier.maxsizeingib;
  }

  get canSubmit() {
    return this.selectedSize.value > this.volume.sizeInGib;
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.volume.project.organization);
  }

  selectSize = (option) => {
    this.selectedSize = option;

    this.fetchCostTask.perform();
  };

  fetchCostTask = restartableTask(async () => {
    if (this.hasResellerPricing) {
      this.cost = { estimated: { price: null, total: null }, currency: null };
      return;
    }

    await timeout(ENV.environment === "test" ? 0 : 250);

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/blockstorage/estimatedcost`,
      method: "POST",
      body: {
        projectkey: this.volume.project.id,
        volumeid: this.volume.id,
        sizeingib: this.selectedSize.value,
      },
    });

    this.cost = response.content.billing;
  });

  submitTask = task(async () => {
    await this.task.perform(this.selectedSize.value);

    this.modal.close();
  });

  <template>
    <Header>
      {{t "block-storage-volumes.details.modal.resize"}}
    </Header>
    <Body>
      {{#if this.isAtMax}}
        <AlertMessage data-test-block-storage-resize-at-max @type="information">
          {{t "block-storage-volumes.details.modal.resize-at-max"}}
        </AlertMessage>
      {{else}}
        <AlertMessage class="mb-4" data-test-block-storage-resize-info @type="information">
          {{t "block-storage-volumes.details.modal.resize-info"}}
        </AlertMessage>
        <TaskForm id="resize" @task={{this.submitTask}}>
          <Label for="block-storage-resize-size">
            {{t "label.size"}}
          </Label>
          <ResourceSlider
            @id="block-storage-resize-size"
            @options={{this.sizeOptions}}
            @selected={{this.selectedSize}}
            @onUpdate={{this.selectSize}}
          >
            {{this.selectedSize.label}}
          </ResourceSlider>
        </TaskForm>
        <div class="w-full min-h-16 flex items-center justify-end pt-2 mt-12 -mb-3 border-gray-200 border-t">
          {{#if this.fetchCostTask.isRunning}}
            <IconsLoadingSpinner class="size-6" />
          {{else if this.cost}}
            {{#unless this.hasResellerPricing}}
              <span class="text-gray-500 text-sm pr-3">
                {{t "title.total-cost"}}
              </span>
            {{/unless}}
            <CostCompact
              data-test-block-storage-resize-cost
              @currency={{this.cost.currency}}
              @price={{this.cost.estimated.price}}
              @discountedPrice={{this.cost.estimated.total}}
            />
          {{/if}}
        </div>
      {{/if}}
    </Body>
    <Footer>
      <GButton class="mr-2" @type="button" @color="gray" {{on "click" (modalClose)}}>
        {{t "button.close"}}
      </GButton>
      <GButton
        data-test-block-storage-resize-submit
        @type="submit"
        @color="blue"
        @disabled={{not this.canSubmit}}
        form="resize"
      >
        {{t "button.update"}}
      </GButton>
    </Footer>
  </template>
}
